import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaLink,
  FaHighlighter,
  FaRemoveFormat,
  FaFont,
  FaTextHeight,
  FaUpload,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { normalizeMarkdown as normalize } from 'components/Reports/utils/normalizeMarkdown';
import { createReportLayerEmbedExtension } from 'components/Reports/mentions/createReportLayerEmbedExtension';
import { createReportMentionExtension } from 'components/Reports/mentions/createReportMentionExtension';
import {
  isReportMentionKind,
  parseMentionId,
  type ReportMentionItem,
  type ReportMentionRef,
} from 'components/Reports/mentions/reportMentionTypes';

// Helper to get computed CSS variable value from _root.scss
const getCssVar = (varName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

// Highlight color palette - uses CSS variables from _root.scss
const getHighlightColors = () => [
  { color: getCssVar('--warning-400'), label: 'Yellow' },
  { color: getCssVar('--error-400'), label: 'Red' },
  { color: getCssVar('--tech-blue-400'), label: 'Blue' },
  { color: getCssVar('--success-400'), label: 'Green' },
  { color: getCssVar('--organizations-orchid'), label: 'Purple' },
  { color: getCssVar('--warning-500'), label: 'Orange' },
];

// Text color palette - uses CSS variables from _root.scss
// Note: White removed as it often exports poorly
const getTextColors = () => [
  { color: getCssVar('--warning-400'), label: 'Yellow' },
  { color: getCssVar('--error-400'), label: 'Red' },
  { color: getCssVar('--tech-blue-400'), label: 'Blue' },
  { color: getCssVar('--success-400'), label: 'Green' },
  { color: getCssVar('--organizations-orchid'), label: 'Purple' },
  { color: getCssVar('--warning-500'), label: 'Orange' },
];

// Font size options - relative sizes for better UX
const fontSizeOptions = [
  { value: '0.875rem', label: 'Small' },
  { value: '1rem', label: 'Normal' },
  { value: '1.25rem', label: 'Large' },
  { value: '1.5rem', label: 'X-Large' },
];

export interface LibraryImage {
  id: string;
  fileName: string;
  fileUrl: string;
  mimetype: string;
  size: string;
  createdAt: string | null;
  updatedAt: string | null;
  signedUrl: string | undefined;
  expiresAt: string | null;
}

interface TiptapRichTextEditorProps {
  value: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minHeight?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  showToolbar?: boolean;
  onClickUploadImage?: (files: File[]) => void | Promise<unknown>;
  imageLibrary?: LibraryImage[]; // Signed URLs for images already stored on the section
  normalizeMarkdown?: boolean; // Whether to normalize markdown content (e.g., from SITREP sections) for better display in the editor
  /**
   * Enable report @-mentions (Resources / Actions). Off by default for
   * non-Reports TipTap.
   */
  mentionsEnabled?: boolean;
  mentionItems?: ReportMentionItem[];
  onMentionClick?: (mention: ReportMentionRef) => void;
  /** TipTap task list (Met/Not met checkboxes). Off by default. */
  enableTaskList?: boolean;
}

export const processImageContentForSave = (
  content: string,
  imageLibrary?: LibraryImage[]
) => {
  if (!imageLibrary) return content;

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.querySelectorAll('img');

  images.forEach((img) => {
    const signedUrl = img.getAttribute('src');

    const fileUrl = imageLibrary.find((image) =>
      signedUrl?.includes(image.fileUrl)
    )?.fileUrl;

    if (fileUrl) {
      img.setAttribute('src', fileUrl);
    }
  });

  return doc.body.innerHTML;
};

const uploadedImageSrcs = (result: unknown): string[] => {
  const items = Array.isArray(result) ? result : result ? [result] : [];
  return items
    .map((item) => {
      if (!item || typeof item !== 'object') return undefined;
      const rec = item as { fileUrl?: string; signedUrl?: string };
      return rec.fileUrl || rec.signedUrl;
    })
    .filter((src): src is string => Boolean(src));
};

export const TiptapRichTextEditor: React.FC<TiptapRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  className,
  minHeight = '150px',
  autoFocus = false,
  readOnly = false,
  showToolbar = true,
  onClickUploadImage,
  imageLibrary,
  normalizeMarkdown = false,
  mentionsEnabled = false,
  mentionItems = [],
  onMentionClick,
  enableTaskList = false,
}) => {
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Compute colors from CSS variables once on mount
  const highlightColors = useMemo(() => getHighlightColors(), []);
  const textColors = useMemo(() => getTextColors(), []);

  const mentionItemsRef = useRef(mentionItems);
  mentionItemsRef.current = mentionItems;
  const onMentionClickRef = useRef(onMentionClick);
  onMentionClickRef.current = onMentionClick;

  const processContentForDisplay = useMemo(() => {
    return (content: string, imageLibrary?: LibraryImage[]) => {
      const cleanContent = normalizeMarkdown ? normalize(content) : content;

      if (!imageLibrary) return cleanContent;

      const parser = new DOMParser();
      const doc = parser.parseFromString(cleanContent, 'text/html');
      const images = doc.querySelectorAll('img');

      if (imageLibrary) {
        const imageMap = new Map(
          imageLibrary.map((image) => [image.fileUrl, image.signedUrl])
        );

        images.forEach((img) => {
          const fileUrl = img.getAttribute('src');

          const signedUrl = fileUrl ? imageMap.get(fileUrl) : undefined;
          if (signedUrl) {
            img.setAttribute('src', signedUrl);
            img.setAttribute('width', '400'); // Set a default width for display purposes
          }
        });
      }

      return doc.body.innerHTML;
    };
  }, [normalizeMarkdown]);

  const handleContentChange = (content: string) => {
    if (onChange) {
      onChange(content);
    }
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mentionExtension = useMemo(() => {
    if (!mentionsEnabled) return null;
    return createReportMentionExtension({
      getItems: () => mentionItemsRef.current,
      suggestionsEnabled: !readOnly && !disabled,
    });
  }, [mentionsEnabled, readOnly, disabled]);

  const layerEmbedExtension = useMemo(
    () => (mentionsEnabled ? createReportLayerEmbedExtension() : null),
    [mentionsEnabled]
  );

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          // Disabled here because they are configured separately below
          link: false,
          underline: false,
        }),
        Underline,
        Highlight.configure({
          multicolor: true,
        }),
        TextStyle,
        Color,
        FontSize,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Link.configure({
          openOnClick: !readOnly,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        Image.configure({
          allowBase64: true,
          HTMLAttributes: {
            class: 'tiptap-editor__inserted-image',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        ...(enableTaskList
          ? [
              TaskList,
              TaskItem.configure({
                nested: false,
              }),
            ]
          : []),
        ...(mentionExtension ? [mentionExtension] : []),
        ...(layerEmbedExtension ? [layerEmbedExtension] : []),
      ],
      content: value,
      editable: !disabled && !readOnly,
      autofocus: autoFocus ? 'end' : false,
      editorProps: {
        handleClick: (_view, _pos, event) => {
          if (!mentionsEnabled || !onMentionClickRef.current) return false;
          const target = event.target as HTMLElement | null;
          const mentionEl = target?.closest?.(
            '[data-type="mention"]'
          ) as HTMLElement | null;
          if (!mentionEl) return false;

          event.preventDefault();
          event.stopPropagation();

          const rawId = mentionEl.getAttribute('data-id');
          const label =
            mentionEl.getAttribute('data-label') ||
            mentionEl.textContent?.replace(/^@/, '') ||
            '';
          const kindAttr = mentionEl.getAttribute('data-mention-kind');
          const parsed = parseMentionId(rawId);
          const kind = isReportMentionKind(kindAttr) ? kindAttr : parsed.kind;
          const id = parsed.id || rawId || '';
          if (!kind || !id) return true;

          onMentionClickRef.current({ kind, id, label });
          return true;
        },
      },
      onUpdate: ({ editor: currentEditor }) => {
        if (handleContentChange) {
          const html = currentEditor.getHTML();
          handleContentChange(html);
        }
      },
    },
    [enableTaskList, layerEmbedExtension, mentionExtension, mentionsEnabled]
  );

  // Update editor content when value prop changes externally
  useEffect(() => {
    const cleanedContent = processContentForDisplay(value, imageLibrary);
    if (editor && cleanedContent !== editor.getHTML()) {
      editor.commands.setContent(cleanedContent);
    }
  }, [value, editor, imageLibrary, processContentForDisplay]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly);
    }
  }, [disabled, readOnly, editor]);

  if (!editor) {
    return null;
  }

  const replaceImageSrc = (from: string, to: string) => {
    const { tr } = editor.state;
    let modified = false;
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs.src === from) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: to });
        modified = true;
      }
    });
    if (modified) {
      editor.view.dispatch(tr);
      handleContentChange(editor.getHTML());
    }
  };

  const removeImageSrc = (src: string) => {
    const { tr } = editor.state;
    const ranges: Array<{ from: number; to: number }> = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs.src === src) {
        ranges.push({ from: pos, to: pos + node.nodeSize });
      }
    });
    for (const range of ranges.reverse()) {
      tr.delete(range.from, range.to);
    }
    if (ranges.length) {
      editor.view.dispatch(tr);
      handleContentChange(editor.getHTML());
    }
  };

  const uploadFilesChangeHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length <= 0) return;
    if (files.length > 10) {
      toast.info('Maximum upload limit is 10 files at a time');
      return;
    }

    setIsUploadingImage(true);
    try {
      for (const file of Array.from(files)) {
        const previewSrc = URL.createObjectURL(file);
        editor.chain().focus().setImage({ src: previewSrc }).run();

        if (!onClickUploadImage) {
          continue;
        }

        try {
          const result = await onClickUploadImage([file]);
          const [persistedSrc] = uploadedImageSrcs(result);
          if (persistedSrc) {
            replaceImageSrc(previewSrc, persistedSrc);
            URL.revokeObjectURL(previewSrc);
          }
        } catch {
          removeImageSrc(previewSrc);
          URL.revokeObjectURL(previewSrc);
        }
      }
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSetLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkDialog(false);
  };

  const openLinkDialog = () => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setShowLinkDialog(true);
  };

  const renderTooltip = (text: string) => (
    <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s/g, '-')}`}>
      {text}
    </Tooltip>
  );

  return (
    <>
      <div
        className={classnames('tiptap-editor', className, {
          'tiptap-editor--disabled': disabled,
          'tiptap-editor--readonly': readOnly,
        })}
        style={readOnly ? undefined : { minHeight }}
      >
        {/* Toolbar - only show in edit mode */}
        {showToolbar && !readOnly && (
          <div className="tiptap-editor__toolbar">
            {/* Text Formatting Group */}
            <div className="tiptap-editor__toolbar-group">
              <OverlayTrigger placement="top" overlay={renderTooltip('Bold')}>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  disabled={!editor.can().chain().focus().toggleBold().run()}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('bold'),
                  })}
                  aria-label="Bold"
                >
                  <FaBold />
                </button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip('Italic')}>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  disabled={!editor.can().chain().focus().toggleItalic().run()}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('italic'),
                  })}
                  aria-label="Italic"
                >
                  <FaItalic />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Underline')}
              >
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  disabled={
                    !editor.can().chain().focus().toggleUnderline().run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('underline'),
                  })}
                  aria-label="Underline"
                >
                  <FaUnderline />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Strikethrough')}
              >
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  disabled={!editor.can().chain().focus().toggleStrike().run()}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('strike'),
                  })}
                  aria-label="Strikethrough"
                >
                  <FaStrikethrough />
                </button>
              </OverlayTrigger>
            </div>

            {/* Highlighting Group */}
            <div className="tiptap-editor__toolbar-group">
              <div className="tiptap-editor__highlight-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Highlight')}
                >
                  <button
                    type="button"
                    onClick={() => setShowHighlightMenu(!showHighlightMenu)}
                    className={classnames('tiptap-editor__button', {
                      'tiptap-editor__button--active':
                        editor.isActive('highlight'),
                    })}
                    aria-label="Highlight"
                  >
                    <FaHighlighter />
                  </button>
                </OverlayTrigger>
                {showHighlightMenu && (
                  <div className="tiptap-editor__color-dropdown">
                    {highlightColors.map(({ color, label }) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().setHighlight({ color }).run();
                          setShowHighlightMenu(false);
                        }}
                        className={classnames('tiptap-editor__color-swatch', {
                          'tiptap-editor__color-swatch--active':
                            editor.isActive('highlight', { color }),
                        })}
                        style={{ backgroundColor: color }}
                        title={label}
                        aria-label={`Highlight ${label}`}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        editor.chain().focus().unsetHighlight().run();
                        setShowHighlightMenu(false);
                      }}
                      className="tiptap-editor__button tiptap-editor__button--clear"
                      title="Remove Highlight"
                      aria-label="Remove Highlight"
                    >
                      <FaRemoveFormat />
                    </button>
                  </div>
                )}
              </div>

              {/* Text Color */}
              <div className="tiptap-editor__highlight-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Text Color')}
                >
                  <button
                    type="button"
                    onClick={() => setShowColorMenu(!showColorMenu)}
                    className={classnames('tiptap-editor__button', {
                      'tiptap-editor__button--active':
                        editor.isActive('textStyle'),
                    })}
                    aria-label="Text Color"
                  >
                    <FaFont />
                  </button>
                </OverlayTrigger>
                {showColorMenu && (
                  <div className="tiptap-editor__color-dropdown">
                    {textColors.map(({ color, label }) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().setColor(color).run();
                          setShowColorMenu(false);
                        }}
                        className={classnames('tiptap-editor__color-swatch', {
                          'tiptap-editor__color-swatch--active':
                            editor.isActive('textStyle', { color }),
                        })}
                        style={{ backgroundColor: color }}
                        title={label}
                        aria-label={`Text Color ${label}`}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        editor.chain().focus().unsetColor().run();
                        setShowColorMenu(false);
                      }}
                      className="tiptap-editor__button tiptap-editor__button--clear"
                      title="Remove Color"
                      aria-label="Remove Color"
                    >
                      <FaRemoveFormat />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Font Size Group */}
            <div className="tiptap-editor__toolbar-group">
              <div className="tiptap-editor__highlight-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Font Size')}
                >
                  <button
                    type="button"
                    onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
                    className="tiptap-editor__button"
                    aria-label="Font Size"
                  >
                    <FaTextHeight />
                  </button>
                </OverlayTrigger>
                {showFontSizeMenu && (
                  <div className="tiptap-editor__font-size-dropdown">
                    {fontSizeOptions.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().setFontSize(value).run();
                          setShowFontSizeMenu(false);
                        }}
                        className={classnames(
                          'tiptap-editor__font-size-option',
                          {
                            'tiptap-editor__font-size-option--active':
                              editor.getAttributes('textStyle').fontSize ===
                              value,
                          }
                        )}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        editor.chain().focus().unsetFontSize().run();
                        setShowFontSizeMenu(false);
                      }}
                      className="tiptap-editor__font-size-option tiptap-editor__font-size-option--reset"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Lists & Alignment Group */}
            <div className="tiptap-editor__toolbar-group">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Bullet List')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('bulletList'),
                  })}
                  aria-label="Bullet List"
                >
                  <FaListUl />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Numbered List')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('orderedList'),
                  })}
                  aria-label="Numbered List"
                >
                  <FaListOl />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Align Left')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign('left').run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive({
                      textAlign: 'left',
                    }),
                  })}
                  aria-label="Align Left"
                >
                  <FaAlignLeft />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Align Center')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign('center').run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive({
                      textAlign: 'center',
                    }),
                  })}
                  aria-label="Align Center"
                >
                  <FaAlignCenter />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Align Right')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign('right').run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive({
                      textAlign: 'right',
                    }),
                  })}
                  aria-label="Align Right"
                >
                  <FaAlignRight />
                </button>
              </OverlayTrigger>
            </div>

            {/* Links & Actions Group */}
            <div className="tiptap-editor__toolbar-group">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Insert Link')}
              >
                <button
                  type="button"
                  onClick={openLinkDialog}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('link'),
                  })}
                  aria-label="Insert Link"
                >
                  <FaLink />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Clear Formatting')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .unsetColor()
                      .unsetHighlight()
                      .clearNodes()
                      .unsetAllMarks()
                      .run()
                  }
                  className="tiptap-editor__button"
                  aria-label="Clear Formatting"
                >
                  <FaRemoveFormat />
                </button>
              </OverlayTrigger>
              {onClickUploadImage && (
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Upload image')}
                >
                  <button
                    type="button"
                    onClick={handleImageUploadClick}
                    className="tiptap-editor__button"
                    aria-label="Upload image"
                    disabled={isUploadingImage}
                  >
                    <FaUpload />
                  </button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        )}

        {/* Editor Content */}
        <div
          className="tiptap-editor__content"
          style={readOnly ? undefined : { minHeight }}
        >
          <EditorContent editor={editor} />
        </div>

        {/* Link Dialog */}
        {showLinkDialog && (
          <div className="tiptap-editor__link-dialog">
            <div className="tiptap-editor__link-dialog-content">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL"
                className="form-control"
                autoFocus
              />
              <div className="tiptap-editor__link-dialog-actions">
                <button
                  type="button"
                  onClick={handleSetLink}
                  className="btn btn-primary btn-sm"
                >
                  Set Link
                </button>
                {editor.isActive('link') && (
                  <button
                    type="button"
                    onClick={handleRemoveLink}
                    className="btn btn-danger btn-sm"
                  >
                    Remove Link
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowLinkDialog(false)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {onClickUploadImage && !readOnly ? (
        <input
          ref={fileInputRef}
          type="file"
          name="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/bmp,image/webp,image/svg+xml,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg"
          className="tw:hidden"
          onChange={uploadFilesChangeHandler}
        />
      ) : null}
    </>
  );
};

TiptapRichTextEditor.displayName = 'TiptapRichTextEditor';

export default TiptapRichTextEditor;
